import { css } from "@emotion/react";
import { useTranslations } from "next-intl";
import { Button } from "react-aria-components";

import { Icon } from "@zapier/zinnia-icons";
import {
  BetaMenuTriggerItem,
  BetaMenuTriggerList,
  BetaMenuTriggerPopover,
  BetaMenuTriggerRoot,
  Colors,
  Shadows,
} from "@zapier/design-system";

import { MAX_RUN_SELECTION } from "@zapier/reporting/src/constants";
import { SelectRunOptions } from "@zapier/reporting/src/types";

type ListSelectionDropdownProps = {
  onSelect: (id: SelectRunOptions) => void;
  value: "all" | "shown" | "none" | null;
  passSelection: boolean;
};

function getCheckboxIcon(value: ListSelectionDropdownProps["value"]) {
  if (value === "all") return "formCheckSquare";
  if (value === "shown") return "formDashSquare";
  return "formBlank";
}

// --zds-size-medium = 2.25rem = 36px (matches DS input height)
// --zds-radius-6 = 0.375rem = 6px
// --zds-space-12 = 0.75rem = 12px
const triggerStyle = (selected: boolean) => css`
  align-items: center;
  background: ${Colors.PrimeWhite};
  border: 1px solid ${selected ? Colors.UiPrimaryStronger : Colors.GrayWarm4};
  border-radius: var(--zds-radius-6);
  cursor: pointer;
  display: flex;
  height: var(--zds-size-medium);
  justify-content: space-between;
  padding: 0 var(--zds-space-12);
  transition: all ease-in 200ms;
  width: 100%;

  &:hover {
    background-color: ${selected ? Colors.UiPrimaryWeakest : Colors.PrimeWhite};
    border-color: ${Colors.UiPrimaryStronger};
    box-shadow: ${Shadows.elevation6};
  }

  &[data-focus-visible] {
    box-shadow:
      0 0 0 2px ${Colors.PrimeWhite},
      0 0 0 4px ${Colors.UiPrimaryStronger};
    outline: none;
  }
`;

const ListSelectionDropdown = (props: ListSelectionDropdownProps) => {
  const t = useTranslations("run-log.list-selection-dropdown");
  const selected = props.value === "all" || props.value === "shown";
  const checkboxIcon = getCheckboxIcon(props.value);

  return (
    <BetaMenuTriggerRoot>
      <Button
        aria-label={t("selection-options-aria-label")}
        css={triggerStyle(selected)}
      >
        <Icon
          name={checkboxIcon}
          size={20}
          color={selected ? "UiPrimaryStronger" : "StrokeStronger"}
        />
        <Icon name="arrowDropdown" size={20} color="GrayWarm10" />
      </Button>
      <BetaMenuTriggerPopover>
        <BetaMenuTriggerList
          aria-label={t("selection-options-aria-label")}
          onAction={(key) => props.onSelect(key as SelectRunOptions)}
        >
          <BetaMenuTriggerItem id="shown">
            {t("select-shown")}
          </BetaMenuTriggerItem>
          <BetaMenuTriggerItem id="all">
            {t("select-all", { count: MAX_RUN_SELECTION })}
          </BetaMenuTriggerItem>
          {props.passSelection && (
            <BetaMenuTriggerItem id="none">
              {t("deselect-all")}
            </BetaMenuTriggerItem>
          )}
        </BetaMenuTriggerList>
      </BetaMenuTriggerPopover>
    </BetaMenuTriggerRoot>
  );
};

export default ListSelectionDropdown;
